import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Dumbbell, PlayCircle, RefreshCw, AlertCircle } from 'lucide-react';

const FALLBACK_IMAGE = '/exercise-media/fallback.svg';

function useFallbackImage(event) {
  if (event.currentTarget.src.endsWith(FALLBACK_IMAGE)) return;
  event.currentTarget.src = FALLBACK_IMAGE;
}

export default function ExercisesPage() {
  const { t } = useApp();
  const [exercises, setExercises] = useState([]);
  const [filters, setFilters] = useState({ muscles: [], equipment: [], difficulties: [] });
  const [muscle, setMuscle] = useState('all');
  const [equipment, setEquipment] = useState('all');
  const [difficulty, setDifficulty] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    api.get('/exercises/filters', { signal: controller.signal })
      .then((r) => setFilters({ muscles: r.data.muscles || r.data.muscle_groups || [], equipment: r.data.equipment || [], difficulties: r.data.difficulties || [] }))
      .catch((requestError) => { if (requestError.code !== 'ERR_CANCELED') setError(t('connectionError')); });
    return () => controller.abort();
  }, [t]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (muscle !== 'all') params.set('muscle', muscle);
    if (equipment !== 'all') params.set('equipment', equipment);
    if (difficulty !== 'all') params.set('difficulty', difficulty);
    if (search.trim()) params.set('search', search.trim());
    const controller = new AbortController();
    const tm = setTimeout(() => {
      setLoading(true);
      setError('');
      api.get(`/exercises?${params}`, { signal: controller.signal })
         .then((r) => setExercises(Array.isArray(r.data) ? r.data : r.data?.items || r.data?.exercises || []))
        .catch((requestError) => { if (requestError.code !== 'ERR_CANCELED') setError(t('connectionError')); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 250);
    return () => { clearTimeout(tm); controller.abort(); };
  }, [muscle, equipment, difficulty, search, reloadKey, t]);

  const diffColor = (d) => d === 'beginner' ? 'text-primary border-primary/40' : d === 'intermediate' ? 'text-[hsl(38,92%,55%)] border-[hsl(38,92%,55%)]/40' : 'text-destructive border-destructive/40';

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4">
      <div className="flex items-center gap-2 mb-1">
        <Dumbbell size={20} className="text-primary" />
        <h1 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{t('exercises')}</h1>
        <Badge variant="outline" className="ms-1 text-[10px] border-primary/40 text-primary">{exercises.length}</Badge>
      </div>
       <p data-testid="exercise-result-count" className="text-xs text-muted-foreground mb-4">{exercises.length} {t('results')}</p>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="exercise-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`${t('search')}...`} className="ps-9" />
        </div>
        <Select value={muscle} onValueChange={setMuscle}>
          <SelectTrigger data-testid="exercise-muscle-filter" className="sm:w-40"><SelectValue placeholder={t('muscle')} /></SelectTrigger>
          <SelectContent>
           <SelectItem value="all">{t('allMuscles')}</SelectItem>
            {filters.muscles.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={equipment} onValueChange={setEquipment}>
          <SelectTrigger data-testid="exercise-equipment-filter" className="sm:w-44"><SelectValue placeholder={t('equipment')} /></SelectTrigger>
          <SelectContent>
           <SelectItem value="all">{t('allEquipment')}</SelectItem>
            {filters.equipment.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={difficulty} onValueChange={setDifficulty}>
          <SelectTrigger data-testid="exercise-difficulty-filter" className="sm:w-40"><SelectValue placeholder={t('difficulty')} /></SelectTrigger>
          <SelectContent>
           <SelectItem value="all">{t('allLevels')}</SelectItem>
            {filters.difficulties.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {error ? (
        <div className="py-14 px-5 text-center rounded-2xl border border-destructive/30 bg-destructive/5">
          <AlertCircle size={32} className="mx-auto mb-3 text-destructive" />
          <p className="text-sm text-muted-foreground">{error}</p>
          <button
            data-testid="exercise-retry-button"
            onClick={() => setReloadKey((value) => value + 1)}
            className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-primary"
          >
            <RefreshCw size={15} /> {t('retry')}
          </button>
        </div>
      ) : loading ? (
        <div className="py-12 text-center text-muted-foreground text-sm">{t('loading')}</div>
       ) : exercises.length === 0 ? (
         <div data-testid="exercise-empty-state" className="py-16 px-5 text-center rounded-2xl border border-dashed border-border bg-card/50">
           <Dumbbell size={34} className="mx-auto mb-3 text-primary/60" />
           <p className="font-semibold">{t('noExercisesFound')}</p>
           <p className="text-xs text-muted-foreground mt-1">{t('tryDifferentFilters')}</p>
           <button data-testid="exercise-clear-filters-button" onClick={() => { setSearch(''); setMuscle('all'); setEquipment('all'); setDifficulty('all'); }} className="mt-4 text-sm font-bold text-primary">{t('clearFilters')}</button>
         </div>
       ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="exercise-grid">
          {exercises.map((ex) => (
            <button key={ex.id} data-testid={`exercise-card-${ex.id}`} onClick={() => { setSelected(ex); setVideoFailed(false); }}
              className="bg-card rounded-xl border border-border overflow-hidden text-start hover:border-primary/40 transition-colors elev-1">
              <div className="h-28 overflow-hidden relative">
                <img src={ex.image || FALLBACK_IMAGE} onError={useFallbackImage} alt={ex.name} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
              </div>
              <div className="p-3">
                <p className="text-sm font-semibold leading-tight line-clamp-2">{ex.name}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  <Badge variant="outline" className="text-[9px] px-1.5 border-primary/40 text-primary">{ex.muscle_group}</Badge>
                  <Badge variant="outline" className={`text-[9px] px-1.5 ${diffColor(ex.difficulty)}`}>{ex.difficulty}</Badge>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5">{ex.equipment}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); setVideoFailed(false); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="pe-6">{selected.name}</DialogTitle>
              </DialogHeader>
              <div className="relative rounded-xl overflow-hidden bg-black">
                {selected.video_placeholder && !videoFailed ? (
                  <video
                    key={selected.video_placeholder}
                    data-testid="exercise-demo-video"
                    src={selected.video_placeholder}
                    poster={selected.image || FALLBACK_IMAGE}
                    className="w-full aspect-video object-cover"
                    controls
                    muted
                    playsInline
                    preload="metadata"
                    onError={() => setVideoFailed(true)}
                  />
                ) : (
                  <img data-testid="exercise-demo-fallback" src={selected.image || FALLBACK_IMAGE} onError={useFallbackImage} alt={selected.name} className="w-full aspect-video object-cover" />
                )}
                <div className="absolute top-2 end-2 flex items-center gap-1 text-[10px] text-white bg-black/60 rounded-full px-2 py-1 pointer-events-none">
                  <PlayCircle size={11} /> {t('exerciseDemo')}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="outline" className="border-primary/40 text-primary text-[10px]">{selected.muscle_group}</Badge>
                {selected.secondary_muscles?.map((m) => <Badge key={m} variant="secondary" className="text-[10px]">{m}</Badge>)}
                <Badge variant="outline" className="text-[10px]">{selected.equipment}</Badge>
                <Badge variant="outline" className={`text-[10px] ${diffColor(selected.difficulty)}`}>{selected.difficulty}</Badge>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-2">{t('stepByStep')}</h4>
                <ol className="space-y-2">
                  {selected.instructions?.map((step, i) => (
                    <li key={i} className="flex gap-2.5 text-sm text-muted-foreground">
                      <span className="w-5 h-5 shrink-0 rounded-full bg-primary/15 text-primary text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
